import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { Link } from 'react-router-dom';
import { theme } from '../../../theme/theme';
import { StatusIcon, StatusType } from '../../global';

const useStatusIconsCard = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 16,
        borderRadius: 5,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        background: theme.colors.white,
        '& > span': {
            color: theme.colors.darkBlue[1],
            ...theme.typography.headings.h6,
            marginBottom: 0,
            textTransform: 'uppercase',
        },
    },
    cells: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
        flex: 1,
        gap: 20,
        '& > *': {
            flex: '0 1 140px',
        },
    },
});

/**
 * Display a list of status icons an associated values.
 *
 * @param props.fetching show fetching skeletons
 * @param props.name card name (optional)
 * @param props.data data groups to display, name is the header, value is the numeric information, type is the
 *        individual status type, and icon the element to be displayed beside the name
 */
export const StatusIconsCard = (props: {
    fetching?: boolean;
    name?: string;
    data: { name: string; value: number; type: StatusType; icon: React.ReactNode; onClick: () => void; href: string }[];
}) => {
    const classes = useStatusIconsCard();

    if (props.fetching)
        return (
            <div className={classes.root}>
                {props.name != undefined && <Skeleton />}
                <div className={classes.cells}>
                    {props.data.length === 0 ? (
                        <>
                            <Skeleton variant='rect' height={60} />
                            <Skeleton variant='rect' height={60} />
                            <Skeleton variant='rect' height={60} />
                            <Skeleton variant='rect' height={60} />
                        </>
                    ) : (
                        props.data.map((_, i) => <Skeleton key={i} variant='rect' height={60} />)
                    )}
                </div>
            </div>
        );

    return (
        <div className={classes.root}>
            {props.name != undefined && <span>{props.name}</span>}
            <div className={classes.cells}>
                {props.data.map((d, i) => (
                    <StatusCell key={i} {...d} />
                ))}
            </div>
        </div>
    );
};

const useStatusCardClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        '& > span': {
            color: theme.colors.black,
            ...theme.typography.displays.d2,
        },
    },
});

/**
 * Render a single data cell of the status icons card.
 *
 * @param props.name name
 * @param props.value value
 * @param props.type status type
 * @param props.icon icon to de displayed beside the name
 */
const StatusCell = (props: {
    name: string;
    value: number;
    type: StatusType;
    icon: React.ReactNode;
    onClick: () => void;
    href: string;
}) => {
    const classes = useStatusCardClasses();

    return (
        <Link to={props.href} className={classes.root} onClick={props.onClick}>
            <span>{props.value}</span>
            <StatusIcon type={props.type} icon={props.icon} colored>
                {props.name}
            </StatusIcon>
        </Link>
    );
};
